import { all, call, getContext, put, select, takeLatest } from 'redux-saga/effects';

import type { MySchoolsGateway, SchoolsPage } from '../infrastructure/my-schools.gateway';
import { mySchoolsRequested, pageChanged, pageSizeChanged } from './my-schools.actions';
import { selectPage, selectPageSize } from './my-schools.selectors';
import { mySchoolsActions } from './my-schools.slice';

const LOAD_ERROR = 'Failed to load schools.';

/** Best-effort human message for the failure banner (no domain error union). */
function toMessage(e: unknown): string {
  if (e instanceof TypeError) {
    return 'Network error; please check your connection and retry.';
  }
  return e instanceof Error && e.message ? e.message : LOAD_ERROR;
}

/**
 * WORKER: fetch the current page. Reads `page` + `pageSize` back from the store
 * (the single source of truth for the fetch) and calls the gateway. `takeLatest`
 * cancels an in-flight fetch when a newer trigger arrives; the abort-latest
 * behaviour the old `useInfiniteApi` had.
 */
function* fetchCurrentPage() {
  const gateway: MySchoolsGateway = yield getContext('mySchoolsGateway');
  const page: number = yield select(selectPage);
  const pageSize: number = yield select(selectPageSize);

  yield put(mySchoolsActions.loadStarted());
  try {
    const result = (yield call(gateway.fetchSchools, { page, pageSize })) as SchoolsPage;
    yield put(mySchoolsActions.loadSucceeded(result));
  } catch (e) {
    yield put(mySchoolsActions.loadFailed(toMessage(e)));
  }
}

/** WORKER: move to a page, then fetch it. */
function* pageChangedWorker(action: ReturnType<typeof pageChanged>) {
  yield put(mySchoolsActions.pageSet(action.payload));
  yield* fetchCurrentPage();
}

/** WORKER: change the page size (resets to page 0), then fetch. */
function* pageSizeChangedWorker(action: ReturnType<typeof pageSizeChanged>) {
  yield put(mySchoolsActions.pageSizeSet(action.payload));
  yield* fetchCurrentPage();
}

/** WATCHERS. */
export function* mySchoolsSaga() {
  yield all([
    takeLatest(mySchoolsRequested.type, fetchCurrentPage),
    takeLatest(pageChanged.type, pageChangedWorker),
    takeLatest(pageSizeChanged.type, pageSizeChangedWorker),
  ]);
}
